import { splitEncoding } from '@contentful/live-preview';
import classNames from 'classnames';

import GenericComponent, {
  type SectionContent,
} from '@/components/GenericComponent/GenericComponent';
import type {
  Contentful,
  SectionStateConstraintsList,
} from '@/contentful/contentful.types';
import useEcommMarket, { type userLocationType } from '@/hooks/geo';
import { getStateConstraintToApply } from '@/utils/constraint';

import useStyles from './Section.styles';

export interface SectionType extends Contentful {
  fields: {
    content: SectionContent[];
    showBottomDividerLine?: boolean;
    backgroundColor:
      | 'LightBlueFractal'
      | 'OffWhiteFractal'
      | 'OffWhite'
      | 'White'
      | 'LightBlue';
    showInMarkets?: 'All Markets' | 'Ecomm Only' | 'Non Ecomm Only';
    abTestingRenderInvisible?: boolean;
    id?: string;
    title?: string;
    isFeatured?: boolean;
  };
}

interface Props {
  section: SectionType;
  userLocation: userLocationType | null;
  sectionPosition?: number;
}

const contentToRender = (
  content: SectionContent[],
  userLocation: userLocationType | null
): SectionContent[] => {
  const result = [] as SectionContent[];
  content?.forEach((content: SectionContent) => {
    if ('constraints' in content.fields) {
      const { default: defaultValue, constraints } = (
        content as SectionStateConstraintsList
      ).fields;
      const contentForUserState = getStateConstraintToApply({
        userLocation,
        defaultValue,
        constraints,
      }) as SectionContent[];
      if (contentForUserState) result.push(...contentForUserState);
    } else result.push(content);
  });

  return result;
};

export function Section({ section, userLocation, sectionPosition }: Props) {
  const { classes } = useStyles();
  const {
    content,
    showBottomDividerLine = false,
    backgroundColor,
    showInMarkets = 'All Markets',
    abTestingRenderInvisible = false,
    id,
    isFeatured,
  } = section?.fields || {};

  const { isInEcommMarket } = useEcommMarket();

  if (
    (isInEcommMarket && showInMarkets === 'Non Ecomm Only') ||
    (!isInEcommMarket && showInMarkets === 'Ecomm Only')
  )
    return null;

  const newContent = contentToRender(content, userLocation);

  return (
    <section
      id={splitEncoding(id || '').cleaned}
      className={classNames([
        showBottomDividerLine && classes.grayLine,
        abTestingRenderInvisible && classes.hide,
        classes[backgroundColor],
      ])}
    >
      {newContent?.map((content, index) => (
        <GenericComponent
          key={content.sys.id}
          content={content}
          isFeatured={isFeatured}
          sectionPosition={sectionPosition}
          componentPosition={index}
        />
      ))}
    </section>
  );
}
